'use client'

import OptimizedImage from '@/components/OptimizedImage'
import { RefObject, useRef, useState } from 'react'
import { useDebounceCallback, useIsClient, useResizeObserver } from 'usehooks-ts'

import { useRandomBanner } from '@/hooks/useRandomBanner'
import { UserProfile } from '@/services/cms'

type ProfileBannerProps = {
  userProfile: Partial<UserProfile>
}

type ContainerSize = {
  width?: number
  height?: number
}

function ProfileBanner({ userProfile }: ProfileBannerProps) {
  const { profile_banner: profileBanner, profile_banner_meta: profileBannerMeta } = userProfile
  const randomBanner = useRandomBanner()
  const containerRef = useRef<HTMLDivElement>(null)
  const [containerSize, setContainerSize] = useState<ContainerSize>({})
  const onResize = useDebounceCallback(setContainerSize, 200)
  const isClient = useIsClient()

  const containerWidth = containerSize.width
  const containerHeight = containerSize.height

  useResizeObserver({
    ref: containerRef as RefObject<HTMLElement>,
    onResize,
  })

  const getInitialY = () => {
    if (containerWidth === undefined || containerHeight === undefined || !profileBannerMeta?.ratio)
      return 0

    const imageRatio = profileBannerMeta.ratio
    const scaledHeight = containerWidth / imageRatio
    if (scaledHeight <= containerHeight) return 0

    const maxDragDistance = scaledHeight - containerHeight
    const minY = -maxDragDistance
    const maxY = 0
    return minY + (maxY - minY) * (profileBannerMeta.yPercent ?? 0)
  }

  return (
    <section data-section="profile-banner" className="px-4 py-2 md:px-8 md:py-4 md:pt-8 lg:px-18">
      <div className="relative aspect-[358/96] size-full min-h-[96px] overflow-hidden rounded-2xl md:aspect-[704/152] lg:aspect-[1296/280]">
        <div
          ref={containerRef}
          className="bg-vive-ghost-white/10 relative size-full overflow-hidden rounded-lg"
        >
          <div
            className="absolute inset-0"
            style={{
              transform: `translateY(${getInitialY()}px)`,
            }}
          >
            {isClient && (
              <OptimizedImage
                src={profileBanner || randomBanner}
                alt="profile banner"
                className="h-auto w-full object-cover"
                fill
                preload
                sizes="(max-width: 768px) 40vw, (max-width: 1024px) 704px, 1296px"
                draggable={false}
              />
            )}
          </div>
        </div>
      </div>
    </section>
  )
}

export default ProfileBanner
